/**
 * MarkdownEditor - PROJ-023 Rich Text Editor
 * Markdown editor with live preview using marked + DOMPurify
 */

import { useState, useCallback, useMemo } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Bold,
  Italic,
  Heading1,
  Heading2,
  List,
  ListOrdered,
  Link,
  Code,
  Quote,
  Minus,
  Eye,
  Edit3,
  FileText,
  type LucideIcon
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  minHeight?: string;
  maxHeight?: string;
  className?: string;
}

// Configure marked for safe rendering
marked.setOptions({
  breaks: true,
  gfm: true,
});

// Toolbar button types
type ToolbarButton = {
  icon: LucideIcon;
  label: string;
  prefix: string;
  suffix: string;
  placeholder: string;
  newLine?: boolean;
};

type ToolbarSeparator = {
  type: 'separator';
};

type ToolbarItem = ToolbarButton | ToolbarSeparator;

// Toolbar button configuration
const toolbarButtons: ToolbarItem[] = [
  { icon: Bold, label: 'Negrita', prefix: '**', suffix: '**', placeholder: 'texto en negrita' },
  { icon: Italic, label: 'Cursiva', prefix: '_', suffix: '_', placeholder: 'texto en cursiva' },
  { icon: Heading1, label: 'Título 1', prefix: '# ', suffix: '', placeholder: 'Título', newLine: true },
  { icon: Heading2, label: 'Título 2', prefix: '## ', suffix: '', placeholder: 'Subtítulo', newLine: true },
  { type: 'separator' },
  { icon: List, label: 'Lista', prefix: '- ', suffix: '', placeholder: 'elemento', newLine: true },
  { icon: ListOrdered, label: 'Lista numerada', prefix: '1. ', suffix: '', placeholder: 'elemento', newLine: true },
  { icon: Quote, label: 'Cita', prefix: '> ', suffix: '', placeholder: 'cita', newLine: true },
  { type: 'separator' },
  { icon: Code, label: 'Código', prefix: '`', suffix: '`', placeholder: 'código' },
  { icon: Link, label: 'Enlace', prefix: '[', suffix: '](url)', placeholder: 'texto del enlace' },
  { icon: Minus, label: 'Línea horizontal', prefix: '\n---\n', suffix: '', placeholder: '' },
];

export function MarkdownEditor({
  value,
  onChange,
  placeholder = 'Escribe tu contenido en Markdown...',
  disabled = false,
  minHeight = '150px',
  maxHeight = '300px',
  className,
}: MarkdownEditorProps) {
  const [activeTab, setActiveTab] = useState<'write' | 'preview'>('write');
  const [textareaRef, setTextareaRef] = useState<HTMLTextAreaElement | null>(null);

  // Parse markdown to HTML
  const htmlContent = useMemo(() => {
    if (!value) return '';
    const rawHtml = marked.parse(value);
    // Sanitize HTML to prevent XSS
    return DOMPurify.sanitize(rawHtml as string, {
      ALLOWED_TAGS: [
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'p', 'br', 'hr',
        'ul', 'ol', 'li',
        'blockquote', 'pre', 'code',
        'strong', 'em', 'del', 's',
        'a', 'img',
        'table', 'thead', 'tbody', 'tr', 'th', 'td',
      ],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class'],
    });
  }, [value]);

  // Insert formatting at cursor position
  const insertFormatting = useCallback((
    prefix: string,
    suffix: string,
    placeholder: string,
    newLine?: boolean
  ) => {
    if (!textareaRef || disabled) return;

    const start = textareaRef.selectionStart;
    const end = textareaRef.selectionEnd;
    const selectedText = value.substring(start, end) || placeholder;

    let newValue: string;
    let newCursorPos: number;

    if (newLine && start > 0 && value[start - 1] !== '\n') {
      // Add newline before if needed
      newValue = value.substring(0, start) + '\n' + prefix + selectedText + suffix + value.substring(end);
      newCursorPos = start + 1 + prefix.length + selectedText.length;
    } else {
      newValue = value.substring(0, start) + prefix + selectedText + suffix + value.substring(end);
      newCursorPos = start + prefix.length + selectedText.length;
    }

    onChange(newValue);

    // Restore focus and selection
    setTimeout(() => {
      if (textareaRef) {
        textareaRef.focus();
        const selectStart = newLine && start > 0 ? start + 1 + prefix.length : start + prefix.length;
        textareaRef.setSelectionRange(selectStart, selectStart + selectedText.length);
      }
    }, 0);
  }, [textareaRef, value, onChange, disabled]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (disabled) return;

    const isMod = e.metaKey || e.ctrlKey;

    if (isMod && e.key === 'b') {
      e.preventDefault();
      insertFormatting('**', '**', 'texto en negrita');
    } else if (isMod && e.key === 'i') {
      e.preventDefault();
      insertFormatting('_', '_', 'texto en cursiva');
    } else if (isMod && e.key === 'k') {
      e.preventDefault();
      insertFormatting('[', '](url)', 'texto del enlace');
    }
  }, [insertFormatting, disabled]);

  return (
    <div className={cn('border rounded-md', className)}>
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'write' | 'preview')}>
        {/* Toolbar */}
        <div className="flex items-center justify-between border-b px-2 py-1 bg-muted/30">
          <TooltipProvider delayDuration={300}>
            <div className="flex items-center gap-0.5">
              {toolbarButtons.map((item, idx) => {
                if ('type' in item && item.type === 'separator') {
                  return (
                    <div key={idx} className="w-px h-5 bg-border mx-1" />
                  );
                }

                const btn = item as ToolbarButton;
                const Icon = btn.icon;
                return (
                  <Tooltip key={idx}>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => insertFormatting(btn.prefix, btn.suffix, btn.placeholder, btn.newLine)}
                        disabled={disabled || activeTab === 'preview'}
                      >
                        <Icon className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">
                      {btn.label}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </TooltipProvider>

          <TabsList className="h-7 bg-transparent">
            <TabsTrigger value="write" className="h-6 px-2 text-xs gap-1">
              <Edit3 className="h-3 w-3" />
              Escribir
            </TabsTrigger>
            <TabsTrigger value="preview" className="h-6 px-2 text-xs gap-1">
              <Eye className="h-3 w-3" />
              Vista previa
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Content */}
        <TabsContent value="write" className="m-0">
          <Textarea
            ref={setTextareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className="border-0 rounded-t-none focus-visible:ring-0 focus-visible:ring-offset-0 resize-none"
            style={{ minHeight, maxHeight }}
          />
        </TabsContent>

        <TabsContent value="preview" className="m-0">
          <ScrollArea style={{ minHeight, maxHeight }} className="p-3">
            {htmlContent ? (
              <div
                className="prose prose-sm dark:prose-invert max-w-none
                  prose-headings:font-semibold prose-headings:mb-2 prose-headings:mt-4
                  prose-p:my-2 prose-p:leading-relaxed
                  prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5
                  prose-blockquote:border-l-primary prose-blockquote:bg-muted/50 prose-blockquote:py-1 prose-blockquote:px-3 prose-blockquote:my-2
                  prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:before:content-none prose-code:after:content-none
                  prose-pre:bg-muted prose-pre:p-3 prose-pre:rounded
                  prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                  prose-hr:my-4"
                dangerouslySetInnerHTML={{ __html: htmlContent }}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                <FileText className="h-4 w-4 mr-2" />
                Sin contenido para previsualizar
              </div>
            )}
          </ScrollArea>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * MarkdownDisplay - Read-only markdown renderer
 */
export function MarkdownDisplay({
  content,
  className,
}: {
  content: string;
  className?: string;
}) {
  const htmlContent = useMemo(() => {
    if (!content) return '';
    const rawHtml = marked.parse(content);
    return DOMPurify.sanitize(rawHtml as string, {
      ALLOWED_TAGS: [
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'p', 'br', 'hr',
        'ul', 'ol', 'li',
        'blockquote', 'pre', 'code',
        'strong', 'em', 'del', 's',
        'a', 'img',
        'table', 'thead', 'tbody', 'tr', 'th', 'td',
      ],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class'],
    });
  }, [content]);

  if (!content) return null;

  return (
    <div
      className={cn(
        'prose prose-sm dark:prose-invert max-w-none',
        'prose-headings:font-semibold prose-headings:mb-2 prose-headings:mt-4',
        'prose-p:my-2 prose-p:leading-relaxed',
        'prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5',
        'prose-blockquote:border-l-primary prose-blockquote:bg-muted/50 prose-blockquote:py-1 prose-blockquote:px-3 prose-blockquote:my-2',
        'prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:before:content-none prose-code:after:content-none',
        'prose-pre:bg-muted prose-pre:p-3 prose-pre:rounded',
        'prose-a:text-primary prose-a:no-underline hover:prose-a:underline',
        'prose-hr:my-4',
        className
      )}
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );
}
